/* eslint-disable restrict-imports/restrict-imports */

import color from 'color';
import muiMakeStyles from '@material-ui/core/styles/makeStyles';
import { Styles, WithStylesOptions } from '@material-ui/core/styles/withStyles';
import { Overrides } from '@material-ui/core/styles/overrides';
import { createTheme, Theme, ThemeOptions } from '@material-ui/core/styles';

import { baseOverrides } from 'react-scale/components/ui/theme';
import { Colors } from 'react-scale/components/legacy-css/global_css';
import { StylesHook } from 'react-scale/components/design-system/styles';

declare module '@material-ui/core/styles' {
  export function createTheme<TCustomTheme>(
    theme: ThemeOptions & TCustomTheme,
  ): Theme & TCustomTheme;
}

declare module '@material-ui/core/styles/createTypography' {
  export interface TypographyUtils {
    htmlFontSize: number;
  }
}

export const NUCLEUS_COLORS = {
  pink1: '#F6F5FA', // Lighter
  pink15: '#FBEAFB',
  pink30: '#EDCAED',
  pink50: '#DD45D3', // Darker
  periwinkle5: '#F0EFFE', // Lighter
  periwinkle25: '#DAD7FD',
  periwinkle50: '#B4AEFC',
  periwinkle75: '#8F86FA',
  periwinkle100: '#6A5DF9', // Darker
  shadow: 'rgba(0, 0, 0, 0.3)',
  shadowLight: 'rgba(0, 0, 0, 0.05)',
  coolGray05: '#F9FAFB',
  coolGray10: '#F3F4F6',
  coolGray20: '#E5E7EB',
  coolGray30: '#D1D5DB',
  coolGray40: '#9CA3AF',
  coolGray50: '#6B7280',
  coolGray60: '#4B5563',
  coolGray70: '#374151',
  coolGray80: '#1F2937',
  coolGray90: '#111827',
  errorDark: 'rgb(198, 40, 40)',
  errorLight: 'rgb(255, 235, 238)',
  successDark: 'rgb(39, 92, 56)',
  successLight: 'rgb(241, 248, 245)',
  warningDark: 'rgb(255, 111, 0)',
  warningLight: 'rgb(255, 248, 225)',
} as const;

// Used to calculate periwinkle gradients
export const NUCLEUS_PERIWINKLE_COLOR_RANGE = {
  minRange: { r: 140, g: 137, b: 243 },
  maxRange: { r: 232, g: 229, b: 253 },
};

export const NucleusTheme = {
  typography: {
    fontSize: 14,
    lineHeight: 1.5,
    fontFamily: 'var(--font-family)',
    fontFamilyMono: 'var(--font-family-code)',
    title: {
      fontSize: 24,
      fontWeight: 500,
      margin: 'unset',
    },
    h1: { fontSize: 28, fontWeight: 500, lineHeight: '36px' },
    h2: { fontSize: 24, fontWeight: 500, lineHeight: '32px' },
    h3: { fontSize: 20, fontWeight: 500, lineHeight: '28px' },
    h4: { fontSize: 18, fontWeight: 500, lineHeight: '26px' },
    h5: { fontSize: 16, fontWeight: 500, lineHeight: '24px' },
    body1: { fontSize: 14, lineHeight: '20px' },
    body2: { fontSize: 12, lineHeight: '18px' },
    subtitle1: { fontSize: 10, lineHeight: '16px' },
    subtitle: {
      fontSize: 12,
      opacity: 0.3,
      margin: 'unset',
    },
    subtitle2: {
      fontSize: 14,
      opacity: 0.5,
      margin: 'unset',
      fontWeight: 300,
    },
    ellipsis: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  },
  palette: {
    primary: {
      light: NUCLEUS_COLORS.periwinkle25,
      main: NUCLEUS_COLORS.periwinkle100,
      dark: color(NUCLEUS_COLORS.periwinkle25).darken(0.2).toString(),
      contrastText: '#fff',
    },
    secondary: {
      light: '#fff',
      main: '#fff',
      dark: NUCLEUS_COLORS.periwinkle25,
      contrastText: NUCLEUS_COLORS.periwinkle75,
    },
    success: {
      light: color(Colors.RemoGreen).lighten(0.2).toString(),
      main: Colors.RemoGreen,
      dark: color(Colors.RemoGreen).darken(0.2).toString(),
      contrastText: '#fff',
    },
    // same as primary
    info: {
      light: color(NUCLEUS_COLORS.periwinkle25).lighten(0.2).toString(),
      main: NUCLEUS_COLORS.periwinkle25,
      dark: color(NUCLEUS_COLORS.periwinkle25).darken(0.2).toString(),
      contrastText: '#fff',
    },
    warning: {
      light: color(Colors.RemoGold).lighten(0.2).toString(),
      main: Colors.RemoGold,
      dark: color(Colors.RemoGold).darken(0.2).toString(),
      contrastText: '#fff',
    },
    error: {
      light: color(Colors.RemoRed).lighten(0.2).toString(),
      main: Colors.RemoRed,
      dark: color(Colors.RemoRed).darken(0.2).toString(),
      contrastText: '#fff',
    },
    background: {
      primary: NUCLEUS_COLORS.pink1,
      one: '#fff',
      two: Colors.RemoGray05,
      three: Colors.RemoGray01,
      gray: '#F9FAFB',
    },
  },
  colors: {
    primary1: NUCLEUS_COLORS.periwinkle100,
    blue: Colors.RemoBlue,
    green: Colors.RemoGreen,
    gold: Colors.RemoGold,
    red: Colors.RemoRed,
    white: '#fff',
    gray01: Colors.RemoGray01,
    gray05: Colors.RemoGray05,
    gray10: Colors.RemoGray10,
    gray20: Colors.RemoGray20,
    gray50: Colors.RemoGray50,
    gray75: Colors.RemoGray75,
    orange: Colors.RemoOrange,
    black: '#000',
    ...NUCLEUS_COLORS,
  },
  spacing: 8,
  borderRadius: 8,
  zIndex: {
    layout: 10,
    low: 20, // Modals
    high: 30, // Modals over modals
    aboveLeaflet: 600, // Leaflet map overlays, leaflet uses ~400 internally
    workflows: 630,
  },
  duration: {
    short: 250,
    medium: 500,
    long: 1000,
  },
  containers: {
    borderRadius: {
      small: 4,
      medium: 8,
      large: 16,
    },
    depth1: 'inset 0px -1px 0px rgba(0, 0, 0, 0.1)',
    elevation0: '0px 0px 4px rgba(17, 24, 39, 0.1)',
    elevation1: '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 4px rgba(0, 0, 0, 0.1)',
    elevation2: '0px 0px 4px rgba(17, 24, 39, 0.1)',
    marginRightForElevation: '2px', // this fixes box getting cut off when using boxShadow with elevation1
    border: '1px solid rgba(0, 0, 0, 0.1)',
    padding: '0 24px',
    sidebarPadding: '0 20px',
    filterSidebarPadding: '0 16px 0 0',
    separator: {
      paddingBottom: 16,
      marginBottom: 16,
      borderBottom: '1px solid #E6E6E6',
    },
  },
  alignment: {
    verticalCenter: {
      top: '50%',
      transform: 'translate(0, -50%)',
    },
    inset: {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    },
  },
  sizes: {
    topBarHeight: 64,
    announcementHeight: 18,
    drawerWidth: 280,
    drawerWidthExtraLong: 1200,
    drawerWidthLong: 300,
    drawerSmallWidth: 64,
    openFooterHeight: 205,
  },
  utils: {
    resetList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
  },
  interactions: {
    borderLeftHighlight: {
      position: 'relative',
      transition: 'all 250ms ease-out',
      '&:before': {
        content: "' '",
        background: NUCLEUS_COLORS.periwinkle100,
        width: 4,
        borderRadius: '0 2px 2px 0',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        transform: 'translate3d(-4px, 0, 0)',
        transition: 'all 250ms ease-out',
      },
    },
    borderLeftHighlightHover: {
      color: NUCLEUS_COLORS.periwinkle100,
      '&:before': {
        transform: 'translate3d(0, 0, 0)',
      },
    },
    borderBottomGrow: {
      position: 'relative',
      '&:before': {
        content: '" "',
        background: NUCLEUS_COLORS.periwinkle100,
        position: 'absolute',
        height: 2,
        bottom: -2,
        left: 0,
        right: 0,
        transform: 'scale(0, 1)',
        transformOrigin: 'left bottom',
        transition: `transform 250ms ease-out`,
      },
    },
    borderBottomGrowHover: {
      '&:before': {
        transform: 'scale(1, 1)',
      },
    },
  },
  lightCard: {
    padding: 16,
    border: 'solid 1px var(--color-RemoGray10)',
    borderRadius: 8,
  },
  contentContainer: {
    padding: 16,
  },
  hideScrollbar: {
    /* Hide scrollbar for Chrome, Safari and Opera */
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '-ms-overflow-style': 'none' /* IE and Edge */,
    scrollbarWidth: 'none' /* Firefox */,
  },
  // WAITING FOR RAPID-NUCLEUS INTEGRATION
  // This code is duplicated in more than one place, please global replace once integration is done
  rapidButton: {
    root: {
      borderRadius: 8,
      padding: '4px 16px !important',
      letterSpacing: 0,

      '&.MuiButton-contained.Mui-disabled': {
        backgroundColor: `${NUCLEUS_COLORS.periwinkle100} !important`,
        opacity: 0.3,
        color: 'white !important',
      },
    },
    label: {
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 500,
    },
    contained: {
      boxShadow: 'none !important',
      fontWeight: 500,
    },
  },
  validatorParamsBuilder: {
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${NUCLEUS_COLORS.coolGray20}`,
    '&:last-child': {
      borderBottom: '0',
    },
  },
} as const;

const productOverrides: Overrides = {
  // WAITING FOR RAPID-NUCLEUS INTEGRATION
  // This code is duplicated in more than one place, please global replace once integration is done
  // MuiButton: {
  //   root: {
  //     borderRadius: 8,
  //     padding: '4px 16px',

  //     '&.MuiButton-contained.Mui-disabled': {
  //       backgroundColor: `${NUCLEUS_COLORS.periwinkle100} !important`,
  //       opacity: 0.3,
  //       color: 'white !important',
  //     },
  //   },
  //   label: {
  //     textTransform: 'none',
  //   },
  //   contained: {
  //     boxShadow: 'none',
  //   },
  // },
  MuiTab: {
    root: {
      fontSize: 14,
      textTransform: 'none',
      minWidth: '0 !important',
      padding: 4,
      '&$selected': {
        '& $wrapper': {
          backgroundColor: Colors.RemoGray05,
          '&:hover': {
            backgroundColor: Colors.RemoGray10,
          },
        },
      },
    },
    wrapper: {
      width: 'auto',
      height: 32,
      display: 'flex',
      padding: '0 16px',
      alignItems: 'center',
      borderRadius: 8,
      border: 'solid 1px transparent',
      '&:hover': {
        color: 'black',
        border: 'solid 1px var(--color-RemoGray05)',
      },
    },
  },
  MuiTabs: {
    indicator: {
      display: 'none',
    },
  },
  MuiChip: {
    label: {
      fontWeight: 'normal',
      fontSize: 12,
    },
    colorPrimary: {
      color: NUCLEUS_COLORS.periwinkle100,
      backgroundColor: NUCLEUS_COLORS.periwinkle5,
    },
  },
  MuiOutlinedInput: {
    root: {
      borderRadius: 8,
    },
  },
  MuiSelect: {
    outlined: {
      borderRadius: 8,
    },
  },
  MuiBadge: {}, // Prevent design-system override.
  MuiIconButton: {}, // Prevent design-system override.
  MuiSwitch: {
    root: {
      width: 40,
      height: 20,
      padding: 0,
      overflow: 'visible',
    },
    switchBase: {
      padding: 0,
      '&$checked': {
        transform: 'translateX(20px)',
        color: '#ffffff !important',
        '& + $track': {
          backgroundColor: `${NUCLEUS_COLORS.periwinkle100} !important`,
          opacity: 1,
          border: 'none',
        },
      },
    },
    thumb: {
      width: 20,
      height: 20,
      boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12)',
    },
    track: {
      backgroundColor: '#999999',
      borderRadius: 20 / 2,
      opacity: 1,
    },
    checked: {},
  },
};

const defaultTheme = createTheme<typeof NucleusTheme>({
  ...NucleusTheme,
  overrides: {
    ...(baseOverrides as Overrides),
    ...(productOverrides as Overrides),
  },
});

export default defaultTheme;

export function makeStyles<Props extends {} = {}, ClassKey extends string = string>(
  styles: Styles<typeof defaultTheme, Props, ClassKey>,
  options?: Omit<WithStylesOptions<typeof defaultTheme>, 'withTheme'>,
) {
  return muiMakeStyles<typeof defaultTheme, Props, ClassKey>(styles, options) as StylesHook<
    Props,
    ClassKey
  >;
}
